import { Avatar, List, Button, Image, message, Upload, Progress, Tooltip } from 'antd';
import { _fetch } from '../../_fetch';
import moment from 'moment';
import { Comment } from '@ant-design/compatible';
import { Select } from 'antd';
import { user_role } from "../../consts";
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import UserInfo from '../userInfo';
import { useGetAllUsers } from '../../utils/queryUtils';
import { useParams, useSearchParams } from 'react-router-dom';
import { trackEvent } from '../../utils/mixpanel';
import {
    PaperClipOutlined,
    FileImageOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FileZipOutlined,
    FileOutlined,
    CloseCircleFilled,
    EyeOutlined,
    EditOutlined,
} from '@ant-design/icons';



const getFileIcon = (file) => {
    const name = (file?.name || file?.originalName || '').toLowerCase();
    const type = file?.type || file?.fileType || '';

    if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(name))
        return <FileImageOutlined style={{ color: '#1677ff', fontSize: 18 }} />;
    if (/\.pdf$/.test(name) || type === 'application/pdf')
        return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />;
    if (/\.(docx?)$/.test(name))
        return <FileWordOutlined style={{ color: '#1677ff', fontSize: 18 }} />;
    if (/\.(xlsx?|csv)$/.test(name))
        return <FileExcelOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
    if (/\.(zip|rar|7z|tar|gz)$/.test(name))
        return <FileZipOutlined style={{ color: '#faad14', fontSize: 18 }} />;
    return <FileOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />;
};

const shortName = (name = '', max = 18) => {
    const parts = name.split('.');
    const ext = parts.length > 1 ? parts.pop() : '';
    const base = parts.join('.');
    if (base.length + ext.length + 1 <= max) return name;
    return `${base.slice(0, max - ext.length - 4)}...${ext ? `.${ext}` : ''}`;
};

const isImage = (file) => {
    const name = (file?.name || file?.originalName || '').toLowerCase();
    const type = file?.type || file?.fileType || '';
    return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(name);
};



function PendingFileChip({ file, onRemove, progress }) {
    const preview = file.preview;
    return (
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: '#f0f5ff',
            border: '1px solid #d6e4ff',
            borderRadius: 6,
            padding: '4px 8px',
            maxWidth: 200,
        }}>
            {preview
                ? <img src={preview} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 3 }} />
                : getFileIcon(file)
            }
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: '#262626', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {shortName(file.name)}
                </div>
                {progress !== undefined && progress < 100 && (
                    <Progress percent={progress} size="small" showInfo={false} strokeWidth={3} />
                )}
            </div>
            {onRemove && (
                <CloseCircleFilled
                    onClick={onRemove}
                    style={{ color: '#bfbfbf', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}
                />
            )}
        </div>
    );
}



function AttachedFileChip({ file }) {
    const img = isImage(file);
    const displayName = (file.name || '').split('---')[0];

    return (
        <Tooltip title={displayName}>
            <div
                style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    background: '#fafafa',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    padding: '8px 10px',
                    width: 100,
                    cursor: 'pointer',
                }}
                onClick={() => !img && window.open(file.path, '_blank')}
            >
                {img
                    ? <Image
                        src={file.path}
                        alt={displayName}
                        style={{ width: 60, height: 50, objectFit: 'cover', borderRadius: 4 }}
                        preview={{ mask: <EyeOutlined /> }}
                    />
                    : <div style={{ width: 60, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', borderRadius: 4 }}>
                        {getFileIcon(file)}
                    </div>
                }
                <span style={{ fontSize: 10, color: '#595959', textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3 }}>
                    {shortName(displayName, 16)}
                </span>
                <span style={{ fontSize: 9, color: '#8c8c8c' }}>
                    {moment.utc(file.createdAt).format('DD MMM YY')}
                </span>
            </div>
        </Tooltip>
    );
}



function ComposedInputBox({ value, onChange, pendingFiles, uploadingIds, onRemoveFile, onBeforeUpload, placeholder }) {
    const [focused, setFocused] = useState(false);

    return (
        <div
            style={{
                border: `1px solid ${focused ? '#1677ff' : '#d9d9d9'}`,
                borderRadius: 8,
                background: '#fff',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: focused ? '0 0 0 2px rgba(22,119,255,0.1)' : 'none',
                overflow: 'hidden',
            }}
        >
            <textarea
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                rows={4}
                style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    resize: 'vertical',
                    padding: '10px 12px 6px',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    background: 'transparent',
                    boxSizing: 'border-box',
                }}
            />

            {/* Staged file chips */}
            {pendingFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 12px 8px' }}>
                    {pendingFiles.map(pf => (
                        <PendingFileChip
                            key={pf.key}
                            file={pf}
                            progress={uploadingIds[pf.key]}
                            onRemove={() => onRemoveFile(pf.key)}
                        />
                    ))}
                </div>
            )}

            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 8px',
                borderTop: '1px solid #f0f0f0',
                background: '#fafafa',
            }}>
                <Upload
                    beforeUpload={onBeforeUpload}
                    showUploadList={false}
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.rar,.txt,.ppt,.pptx"
                >
                    <Button
                        icon={<PaperClipOutlined />}
                        type="text"
                        size="small"
                        style={{ color: '#595959', fontSize: 12 }}
                    >
                        Attach file
                    </Button>
                </Upload>
                {pendingFiles.length > 0 && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#8c8c8c' }}>
                        {pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''} attached
                    </span>
                )}
            </div>
        </div>
    );
}



export default function CommentView({
    queryId,
    query_id = null,
    comments = null,
    setComments = null,
    sendEmail,
    fy = null,
    onCommentAdded = null,
    show = true,
    setShow = () => { },
    selectedPrimaryClient = false,
}) {
    const { Option } = Select;
    const [newComment, setNewComment] = useState('');
    const [toggle, setToggle] = useState(false);
    const { companyName } = useParams();
    const userData = useGetAllUsers(false, {}, companyName)?.data;
    const all_users = userData?.data;
    const { company: selected } = useSelector((st) => st.selected);
    const [searchParams] = useSearchParams();
    const activeTab = useState(searchParams.get('tab') || 'queries');
    const [, setSeed] = useState(1);
    const { role, _id: currentUserId } = useSelector((st) => st.user);
    const [user, setUser] = useState([]);

    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploadingIds, setUploadingIds] = useState({});
    const uploadUrlRef = useRef({});

    const [commentFiles, setCommentFiles] = useState({});
    const filesLoadedRef = useRef(false);

    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editText, setEditText] = useState('');
    const [editPendingFiles, setEditPendingFiles] = useState([]);
    const [editUploadingIds, setEditUploadingIds] = useState({});
    const editUploadUrlRef = useRef({});


    useEffect(() => {
        if (!comments?.length || filesLoadedRef.current) return;

        const fetchFiles = async () => {
            try {
                const id = queryId || query_id;
                const param = id ? `query=${id}` : fy ? `fy=${fy}` : null;
                if (!param) return;

                const res = await _fetch(`${process.env.REACT_APP_API_URL}/file?${param}`);
                if (res.status === 200) {
                    const data = await res.json();
                    const filesArr = data.response || [];
                    const grouped = {};
                    filesArr.forEach(f => {
                        if (!f.commentId) return;
                        if (!grouped[f.commentId]) grouped[f.commentId] = [];
                        grouped[f.commentId].push(f);
                    });
                    setCommentFiles(grouped);
                    filesLoadedRef.current = true;
                }
            } catch (e) {
                console.error(e);
            }
        };

        fetchFiles();
    }, [comments]);

    const handleChange = (value) => setUser([...value]);

    useEffect(() => {
        if (newComment?.length === 0) {
            setUser([]);
            setToggle(false);
        }
    }, [newComment]);


    const handleBeforeUpload = async (file) => {
        const key = `${file.name}-${Date.now()}-${Math.random()}`;
        file._key = key;

        let preview = null;
        if (file.type?.startsWith('image/')) {
            preview = URL.createObjectURL(file);
        }

        try {
            const id = query_id || queryId;
            const parts = file.name.split('.');
            const ext = parts.pop();
            const baseName = parts.join('.');
            const uniqueName = `${baseName}-${moment().valueOf()}.${ext}`;

            const ctxParam = id ? `query=${id}` : fy ? `fy=${fy}` : null;
            if (!ctxParam) return false;

            const urlRes = await _fetch(
                `${process.env.REACT_APP_API_URL}/file/get_upload_url?name=${encodeURIComponent(uniqueName)}&${ctxParam}`
            );
            if (urlRes.status === 200) {
                const signedUrl = await urlRes.json();
                uploadUrlRef.current[key] = {
                    signedUrl,
                    contentType: file.type || 'application/octet-stream',
                };
            }
        } catch (e) {
            console.error('Error pre-fetching upload URL', e);
        }

        setPendingFiles(prev => [...prev, { file, preview, key, name: file.name }]);
        return false;
    };

    const removePendingFile = (key) => {
        setPendingFiles(prev => {
            const f = prev.find(p => p.key === key);
            if (f?.preview) URL.revokeObjectURL(f.preview);
            return prev.filter(p => p.key !== key);
        });
        delete uploadUrlRef.current[key];
    };

    const clearInput = () => {
        setNewComment('');
        setUser([]);
        setToggle(false);
        pendingFiles.forEach(pf => pf.preview && URL.revokeObjectURL(pf.preview));
        setPendingFiles([]);
        uploadUrlRef.current = {};
    };


    const handleEditBeforeUpload = async (file) => {
        const key = `${file.name}-${Date.now()}-${Math.random()}`;
        file._key = key;

        let preview = null;
        if (file.type?.startsWith('image/')) {
            preview = URL.createObjectURL(file);
        }

        try {
            const id = query_id || queryId;
            const parts = file.name.split('.');
            const ext = parts.pop();
            const baseName = parts.join('.');
            const uniqueName = `${baseName}-${moment().valueOf()}.${ext}`;

            const ctxParam = id ? `query=${id}` : fy ? `fy=${fy}` : null;
            if (!ctxParam) return false;

            const urlRes = await _fetch(
                `${process.env.REACT_APP_API_URL}/file/get_upload_url?name=${encodeURIComponent(uniqueName)}&${ctxParam}`
            );
            if (urlRes.status === 200) {
                const signedUrl = await urlRes.json();
                editUploadUrlRef.current[key] = {
                    signedUrl,
                    contentType: file.type || 'application/octet-stream',
                };
            }
        } catch (e) {
            console.error('Error pre-fetching edit upload URL', e);
        }

        setEditPendingFiles(prev => [...prev, { file, preview, key, name: file.name }]);
        return false;
    };

    const removeEditPendingFile = (key) => {
        setEditPendingFiles(prev => {
            const f = prev.find(p => p.key === key);
            if (f?.preview) URL.revokeObjectURL(f.preview);
            return prev.filter(p => p.key !== key);
        });
        delete editUploadUrlRef.current[key];
    };

    const cancelEdit = () => {
        setEditingCommentId(null);
        setEditText('');
        editPendingFiles.forEach(pf => pf.preview && URL.revokeObjectURL(pf.preview));
        setEditPendingFiles([]);
        editUploadUrlRef.current = {};
        setEditUploadingIds({});
    };

    const handleEditSubmit = async (commentId) => {
        const id = query_id || queryId;

        for (const pf of editPendingFiles) {
            const { file, key } = pf;
            const meta = editUploadUrlRef.current[key];
            if (!meta) continue;

            try {
                setEditUploadingIds(prev => ({ ...prev, [key]: 10 }));

                const s3Res = await fetch(meta.signedUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': meta.contentType },
                    body: file,
                });

                setEditUploadingIds(prev => ({ ...prev, [key]: 70 }));

                if (s3Res.status === 200) {
                    const parsedUrl = new URL(meta.signedUrl);
                    const pathname = parsedUrl.pathname.slice(1);

                    const fileBody = { path: pathname, type: meta.contentType, commentId };
                    if (id) fileBody.query = id;
                    else if (fy) fileBody.fy = fy;

                    const fileRes = await _fetch(`${process.env.REACT_APP_API_URL}/file`, {
                        method: 'POST',
                        body: fileBody,
                    });

                    if (fileRes.status === 201) {
                        const savedFile = await fileRes.json();
                        setCommentFiles(prev => ({
                            ...prev,
                            [commentId]: [...(prev[commentId] || []), savedFile],
                        }));
                    }
                }

                setEditUploadingIds(prev => ({ ...prev, [key]: 100 }));
            } catch (e) {
                console.error('Edit file upload error', e);
                message.error(`Failed to upload ${file.name}`);
            } finally {
                setEditUploadingIds(prev => {
                    const n = { ...prev };
                    delete n[key];
                    return n;
                });
            }
        }

        // Patch comment text
        try {
            const res = await _fetch(`${process.env.REACT_APP_API_URL}/comment/${commentId}`, {
                method: 'PATCH',
                body: { text: editText },
            });
            if (res.status === 200) {
                const updated = await res.json();
                setComments(prev =>
                    prev.map(c => c._id === commentId ? { ...c, text: updated.text } : c)
                );
                message.success('Comment updated');
            } else {
                message.error('Failed to update comment');
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to update comment');
        }

        // Reset edit state
        cancelEdit();
    };


    async function handleNewComment() {
        const uploadedFiles = [];

        // Upload staged files to S3 first
        if (pendingFiles.length > 0) {
            const id = query_id || queryId;
            for (const pf of pendingFiles) {
                const { file, key } = pf;
                const meta = uploadUrlRef.current[key];
                if (!meta) continue;
                try {
                    setUploadingIds(prev => ({ ...prev, [key]: 10 }));
                    const s3Res = await fetch(meta.signedUrl, {
                        method: 'PUT',
                        headers: { 'Content-Type': meta.contentType },
                        body: file,
                    });
                    setUploadingIds(prev => ({ ...prev, [key]: 70 }));
                    if (s3Res.status === 200) {
                        const parsedUrl = new URL(meta.signedUrl);
                        const pathname = parsedUrl.pathname.slice(1);
                        uploadedFiles.push({ key, pathname, contentType: meta.contentType, name: file.name });
                    }
                    setUploadingIds(prev => ({ ...prev, [key]: 100 }));
                } catch (e) {
                    console.error('S3 upload error', e);
                    message.error(`Failed to upload ${file.name}`);
                }
            }
        }

        // Build payload
        var payload = {};
        if (query_id || queryId) {
            payload = {
                query: queryId,
                query_id: query_id,
                text: newComment,
                isPrimaryClientComments: activeTab[0] === 'primary_client_queries',
            };
        } else if (fy) {
            payload = { fy, text: newComment, arr: user };
            if (selectedPrimaryClient?._id) {
                payload.arr = [selectedPrimaryClient._id];
                payload.isPrimaryClientComments = true;
            }
        }

        try {
            let res = await _fetch(`${process.env.REACT_APP_API_URL}/comment`, {
                method: 'POST',
                body: { ...payload },
            });

            if (res.status === 201) {
                let result = await res.json();
                const commentId = result._id;

                // Save file records in DB
                for (const uf of uploadedFiles) {
                    const id = query_id || queryId;
                    const fileBody = { path: uf.pathname, type: uf.contentType, commentId };
                    if (id) fileBody.query = id;
                    else if (fy) fileBody.fy = fy;

                    const fileRes = await _fetch(`${process.env.REACT_APP_API_URL}/file`, {
                        method: 'POST',
                        body: fileBody,
                    });
                    if (fileRes.status === 201) {
                        const savedFile = await fileRes.json();
                        setCommentFiles(prev => ({
                            ...prev,
                            [commentId]: [...(prev[commentId] || []), savedFile],
                        }));
                    }
                }

                // Trigger email if needed
                if (sendEmail && fy) {
                    await _fetch(`${process.env.REACT_APP_API_URL}/comment/${commentId}/send-email`, {
                        method: 'POST',
                    });
                }

                message.success(sendEmail ? 'Email sent successfully' : 'Comment added successfully');
                setNewComment('');
                setComments(c => [result, ...c]);
                setUser([]);
                setToggle(false);
                onCommentAdded && onCommentAdded({ status: 'OPEN' });
                setShow(false);
                setPendingFiles([]);
                uploadUrlRef.current = {};
                setUploadingIds({});

                trackEvent('Add Comment', { query_id, comment_id: commentId, query: queryId, text: newComment, fy });
                return commentId;
            }
        } catch (e) {
            console.error(e);
        }
    }

    const hasContent = newComment.trim().length > 0 || pendingFiles.length > 0;
    const isUploading = Object.keys(uploadingIds).length > 0;
    const isEditUploading = Object.keys(editUploadingIds).length > 0;

    // const canEdit = !sendEmail && item?.created_by?._id === currentUserId;

    return (
        <React.Fragment>
            <div>
                <ComposedInputBox
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    pendingFiles={pendingFiles}
                    uploadingIds={uploadingIds}
                    onRemoveFile={removePendingFile}
                    onBeforeUpload={handleBeforeUpload}
                    placeholder={sendEmail ? 'Type your email message...' : 'Type your comment...'}
                />

                {hasContent && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        marginTop: 10,
                        flexWrap: 'wrap',
                        gap: 8,
                    }}>
                        <Button onClick={clearInput}>Cancel</Button>

                        {toggle && role !== user_role.PRIMARY_CLIENT && !selectedPrimaryClient?.id && (
                            <Select
                                mode="multiple"
                                showSearch
                                style={{ minWidth: 300 }}
                                placeholder="Select recipients"
                                defaultValue={user}
                                onChange={handleChange}
                                optionLabelProp="label"
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
                                }
                            >
                                {all_users
                                    ?.filter(e =>
                                        e.role === user_role.ADMIN ||
                                        e.company_ids?.some(f => f._id === selected._id)
                                    )
                                    .map(elem => (
                                        <Option
                                            key={elem._id}
                                            label={`${elem.first_name} ${elem.last_name}`}
                                            value={elem._id}
                                        >
                                            <UserInfo
                                                firstName={elem.first_name}
                                                lastName={elem.last_name}
                                                email={elem.email}
                                            />
                                        </Option>
                                    ))}
                            </Select>
                        )}

                        <Button
                            type="primary"
                            loading={isUploading}
                            disabled={!selectedPrimaryClient?._id && toggle && !user?.length}
                            onClick={async () => {
                                if (selectedPrimaryClient?._id) {
                                    let commentId = await handleNewComment();
                                    if (commentId) window.myFileUpload?.(commentId);
                                    return;
                                }
                                if (!toggle && role === user_role.PRIMARY_CLIENT && sendEmail) {
                                    let commentId = await handleNewComment();
                                    if (commentId) window.myFileUpload?.(commentId);
                                } else if (!toggle && sendEmail) {
                                    setToggle(true);
                                } else {
                                    let commentId = await handleNewComment();
                                    if (commentId) window.myFileUpload?.(commentId);
                                }
                            }}
                        >
                            {sendEmail ? 'Send Email' : 'Submit'}
                        </Button>
                    </div>
                )}
            </div>

            {comments ? (
                <List
                    className="comment-list w-100 px-3"
                    itemLayout="horizontal"
                    dataSource={comments}
                    pagination={{ pageSize: 10 }}
                    style={{ marginTop: 16 }}
                    renderItem={(item) => {
                        const filesForComment = commentFiles[item._id] || [];
                        const canEdit = !sendEmail && item?.created_by?._id === currentUserId;
                        const isEditing = editingCommentId === item._id;

                        return (
                            <Comment
                                style={{
                                    margin: '9px 0',
                                    backgroundColor: '#FAFAFA',
                                    paddingLeft: 10,
                                    borderRadius: 8,
                                    border: '1px solid #f0f0f0',
                                }}
                                author={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <b>{item?.created_by?.first_name} {item?.created_by?.last_name}</b>
                                            <span className="ms-2" style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 12 }}>
                                                {moment(item?.createdAt).format('DD MMM YYYY HH:mm')}
                                            </span>
                                        </div>

                                      
                                        {canEdit && !isEditing && (
                                            <Tooltip title="Edit comment">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                  
                                                    style={{ color: '#1677ff', fontSize: 12,marginLeft: 10 }}
                                                    onClick={() => {
                                                        setEditingCommentId(item._id);
                                                        setEditText(item.text || '');
                                                        setEditPendingFiles([]);
                                                        editUploadUrlRef.current = {};
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </div>
                                }
                                avatar={
                                    <Avatar shape="square" style={{ background: '#1677ff' }}>
                                        {item?.created_by?.first_name?.[0]}
                                        {item?.created_by?.last_name?.[0]}
                                    </Avatar>
                                }
                                content={
                                    <div style={{ backgroundColor: '#FAFAFA' }}>
                                        {isEditing ? (
                                            /* ── Edit mode ── */
                                            <div>
                                                <ComposedInputBox
                                                    value={editText}
                                                    onChange={e => setEditText(e.target.value)}
                                                    pendingFiles={editPendingFiles}
                                                    uploadingIds={editUploadingIds}
                                                    onRemoveFile={removeEditPendingFile}
                                                    onBeforeUpload={handleEditBeforeUpload}
                                                    placeholder="Edit comment..."
                                                />

                                                {/* Existing attachments shown read-only */}
                                                {filesForComment.length > 0 && (
                                                    <div>
                                                        <p style={{ margin: '8px 0 4px', fontSize: 11, color: '#8c8c8c' }}>
                                                            Existing attachments:
                                                        </p>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                            {filesForComment.map((f, i) => (
                                                                <AttachedFileChip key={f._id || i} file={f} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                                                    <Button size="small" onClick={cancelEdit}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        loading={isEditUploading}
                                                        disabled={!editText.trim() && editPendingFiles.length === 0}
                                                        onClick={() => handleEditSubmit(item._id)}
                                                    >
                                                        Save
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            /* ── Read mode ── */
                                            <>
                                                <span style={{ whiteSpace: 'pre-line', fontSize: 12, fontWeight: 400 }}>
                                                    {item?.text}
                                                </span>

                                                {role !== user_role.PRIMARY_CLIENT && item?.created_for?.length > 0 && (
                                                    <p style={{ marginTop: 6, marginBottom: 4 }}>
                                                        <span style={{ color: '#6FBAFF', fontWeight: 600, fontSize: 12 }}>Recipients: </span>
                                                        <span style={{ color: '#00000073', fontWeight: 700, fontSize: 12 }}>
                                                            {item.created_for.map(el => `${el?.first_name} ${el?.last_name}`).join(', ')}
                                                        </span>
                                                    </p>
                                                )}

                                                {filesForComment.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                                                        {filesForComment.map((f, i) => (
                                                            <AttachedFileChip key={f._id || i} file={f} />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                }
                            />
                        );
                    }}
                />
            ) : null}
        </React.Fragment>
    );
}